import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  UsersIcon,
  UserPlusIcon,
  SearchIcon,
  ShieldIcon,
  EyeIcon,
  KeyIcon,
  LockIcon,
  PlusIcon,
  ActivityIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertCircleIcon,
  CalendarIcon,
  MailIcon,
  XIcon,
  Loader2Icon,
  UserCheckIcon,
  UserXIcon,
  Trash2Icon,
} from 'lucide-react'
import { useState, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  ResponsiveActionRow,
  ResponsivePageHeader,
  ResponsiveTabs,
} from '@nebula/shared/components'
import { ConfirmModal } from '@nebula/shared/components/modals'
import { usePermissions } from '@nebula/shared/hooks'
import { invitationApi, type Invitation } from '@nebula/shared/services/invitationApi'
import { logsApi, type LogsFilters } from '@nebula/shared/services/logsApi'
import { rbacApi, type Role, type Permission } from '@nebula/shared/services/rbacApi'
import { userApi, type UserData } from '@nebula/shared/services/userApi'
import { useAuthStore } from '@nebula/shared/store'

type TabId = 'members' | 'roles' | 'permissions' | 'audit'

const VALID_TABS = new Set<TabId>(['members', 'roles', 'permissions', 'audit'])
const TEAM_TABS = [
  { id: 'members' as const, label: 'Membros', icon: UsersIcon },
  { id: 'roles' as const, label: 'Funções', icon: ShieldIcon },
  { id: 'permissions' as const, label: 'Permissões', icon: KeyIcon },
  { id: 'audit' as const, label: 'Audit Trail', icon: ActivityIcon },
]

const TeamPage = () => {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user: currentUser, activeOrganizationId } = useAuthStore()
  const { has } = usePermissions()
  const canInviteMembers = has('users:create')
  const canManageMembers = has('users:update')
  const canManageRoles = has('roles:create') || has('roles:manage')

  const activeTab: TabId = VALID_TABS.has(searchParams.get('tab') as TabId)
    ? (searchParams.get('tab') as TabId)
    : 'members'

  const setActiveTab = useCallback(
    (tab: TabId) => {
      setSearchParams({ tab }, { replace: true })
    },
    [setSearchParams],
  )

  // Membros state
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [selectedMember, setSelectedMember] = useState<UserData | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteForm, setInviteForm] = useState({ email: '', role_id: '' })
  const [inviteError, setInviteError] = useState('')
  const [inviteSuccess, setInviteSuccess] = useState('')
  const [memberToRemove, setMemberToRemove] = useState<UserData | null>(null)

  // Roles state
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false)
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permission_ids: [] as string[],
  })
  const [roleError, setRoleError] = useState('')

  // Audit state
  const [auditSource, setAuditSource] = useState('')
  const [auditLimit, setAuditLimit] = useState(50)

  // ============================================================================
  // DATA QUERIES
  // ============================================================================

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => userApi.getUsers(),
  })

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['rbac-roles'],
    queryFn: () => rbacApi.getRoles(),
  })

  const { data: permissions = [], isLoading: permissionsLoading } = useQuery({
    queryKey: ['rbac-permissions'],
    queryFn: () => rbacApi.getPermissions({ limit: 500 }),
  })

  const { data: selectedMemberRoles = [] } = useQuery({
    queryKey: ['user-roles', selectedMember?.id],
    queryFn: () => rbacApi.getUserRoles(selectedMember!.id),
    enabled: !!selectedMember,
  })

  const { data: pendingInvitations = [] } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => invitationApi.list(),
  })

  const auditFilters: LogsFilters = useMemo(() => {
    const f: LogsFilters = { limit: auditLimit }
    if (auditSource) f.source = auditSource
    return f
  }, [auditSource, auditLimit])

  const { data: auditLogs = [], isLoading: auditLoading } = useQuery({
    queryKey: ['org-audit-logs', auditFilters],
    queryFn: () => logsApi.getOrganizationLogs(auditFilters),
    enabled: activeTab === 'audit',
  })

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  const createInvitationMutation = useMutation({
    mutationFn: async (form: typeof inviteForm) => {
      return invitationApi.create({ email: form.email, role_id: form.role_id })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      setShowInviteModal(false)
      setInviteForm({ email: '', role_id: '' })
      setInviteError('')
      setInviteSuccess('Convite enviado com sucesso!')
      setTimeout(() => setInviteSuccess(''), 5000)
    },
    onError: (err: any) => {
      setInviteError(err.response?.data?.detail || 'Erro ao enviar convite')
    },
  })

  const cancelInvitationMutation = useMutation({
    mutationFn: (invitationId: string) => invitationApi.cancel(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
  })

  const activateUserMutation = useMutation({
    mutationFn: (userId: string) => userApi.activateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['org-audit-logs'] })
    },
  })

  const deactivateUserMutation = useMutation({
    mutationFn: (userId: string) => userApi.deactivateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['org-audit-logs'] })
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => userApi.removeMembership(userId, activeOrganizationId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['org-audit-logs'] })
      setMemberToRemove(null)
      setSelectedMember(null)
    },
  })

  const createRoleMutation = useMutation({
    mutationFn: (form: typeof roleForm) =>
      rbacApi.createRole({
        name: form.name,
        description: form.description,
        permission_ids: form.permission_ids,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rbac-roles'] })
      queryClient.invalidateQueries({ queryKey: ['org-audit-logs'] })
      setShowCreateRoleModal(false)
      setRoleForm({ name: '', description: '', permission_ids: [] })
      setRoleError('')
    },
    onError: (err: any) => {
      setRoleError(err.response?.data?.detail || 'Erro ao criar função')
    },
  })

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchesSearch =
        !searchTerm ||
        u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesRole = filterRole === 'all' || (u.roles && u.roles.includes(filterRole))

      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'active' && u.is_active) ||
        (filterStatus === 'inactive' && !u.is_active)

      return matchesSearch && matchesRole && matchesStatus
    })
  }, [users, searchTerm, filterRole, filterStatus])

  const permissionsByResource = useMemo(() => {
    const groups: Record<string, Permission[]> = {}
    permissions.forEach((p) => {
      if (!groups[p.resource]) groups[p.resource] = []
      groups[p.resource].push(p)
    })
    return groups
  }, [permissions])

  // ============================================================================
  // HELPERS
  // ============================================================================

  const getInitials = (name: string | undefined) => {
    if (!name) return '?'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
          <CheckCircleIcon className="mr-1 size-3" /> Ativo
        </span>
      )
    }
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
        <XCircleIcon className="mr-1 size-3" /> Inativo
      </span>
    )
  }

  const getLevelBadge = (level: string) => {
    const colors: Record<string, string> = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      critical: 'bg-red-200 text-red-900',
      debug: 'bg-gray-100 text-gray-800',
    }
    return colors[level] || 'bg-gray-100 text-gray-800'
  }

  const getRolesUsingPermission = (permissionId: string) => {
    return roles.filter((r) => r.permissions?.some((p) => p.id === permissionId)).length
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const formatDateTime = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return dateStr
    }
  }

  // ============================================================================
  // TABS RENDERING
  // ============================================================================

  const renderMembersTab = () => (
    <div className="space-y-6">
      {/* Header Actions */}
      <ResponsiveActionRow>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:gap-3">
          <div className="relative">
            <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
            <input
              type="text"
              placeholder="Buscar membros..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:ring-2 focus:ring-purple-500 sm:w-auto"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500 sm:w-auto"
          >
            <option value="all">Todas as funções</option>
            {roles.map((role) => (
              <option key={role.id} value={role.name}>
                {role.name}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500 sm:w-auto"
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
        {canInviteMembers && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 sm:w-auto"
          >
            <UserPlusIcon className="size-4" />
            <span>Convidar Membro</span>
          </button>
        )}
      </ResponsiveActionRow>

      {/* Success Message */}
      {inviteSuccess && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          <CheckCircleIcon className="size-4" />
          {inviteSuccess}
        </div>
      )}

      {/* Pending Invitations */}
      {pendingInvitations.filter((inv: Invitation) => inv.status === 'pending').length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <h3 className="mb-3 text-sm font-medium text-yellow-800">Convites Pendentes</h3>
          <div className="space-y-2">
            {pendingInvitations
              .filter((inv: Invitation) => inv.status === 'pending')
              .map((inv: Invitation) => (
                <div
                  key={inv.id}
                  className="flex flex-col gap-3 rounded-lg border border-yellow-100 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <MailIcon className="size-4 text-yellow-600" />
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-900">{inv.email}</span>
                      <span className="ml-2 text-xs text-gray-500">Função: {inv.role_name}</span>
                    </div>
                  </div>
                  {canInviteMembers && (
                    <button
                      onClick={() => cancelInvitationMutation.mutate(inv.id)}
                      className="text-xs font-medium text-red-500 hover:text-red-700"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Members Table */}
      {usersLoading ? (
        <div className="flex justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="divide-y divide-gray-200 md:hidden">
            {filteredUsers.map((member) => (
              <div key={member.id} className="space-y-3 p-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 shrink-0">
                    {member.profile_photo_url ? (
                      <img
                        className="size-10 rounded-full object-cover"
                        src={member.profile_photo_url}
                        alt=""
                      />
                    ) : (
                      <div className="flex size-10 items-center justify-center rounded-full bg-purple-100">
                        <span className="text-sm font-medium text-purple-700">
                          {getInitials(member.full_name)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">
                      {member.full_name}
                    </div>
                    <div className="truncate text-sm text-gray-500">{member.email}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {member.roles && member.roles.length > 0 ? (
                    member.roles.map((roleName, idx) => (
                      <span
                        key={idx}
                        className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800"
                      >
                        {roleName}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-400">Sem função</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Entrada: {formatDate(member.created_at)}</span>
                  {getStatusBadge(member.is_active)}
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={() => setSelectedMember(member)}
                    className="text-purple-600 hover:text-purple-900"
                    title="Ver detalhes"
                  >
                    <EyeIcon className="size-4" />
                  </button>
                  {canManageMembers &&
                    member.id !== currentUser?.id &&
                    (member.is_active ? (
                      <button
                        onClick={() => deactivateUserMutation.mutate(member.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Desativar"
                      >
                        <UserXIcon className="size-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => activateUserMutation.mutate(member.id)}
                        className="text-green-500 hover:text-green-700"
                        title="Ativar"
                      >
                        <UserCheckIcon className="size-4" />
                      </button>
                    ))}
                  {canManageMembers && member.id !== currentUser?.id && (
                    <button
                      onClick={() => setMemberToRemove(member)}
                      className="text-red-400 hover:text-red-600"
                      title="Remover membro"
                    >
                      <Trash2Icon className="size-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Membro
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Funções
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Data de Entrada
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredUsers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="size-10 shrink-0">
                          {member.profile_photo_url ? (
                            <img
                              className="size-10 rounded-full object-cover"
                              src={member.profile_photo_url}
                              alt=""
                            />
                          ) : (
                            <div className="flex size-10 items-center justify-center rounded-full bg-purple-100">
                              <span className="text-sm font-medium text-purple-700">
                                {getInitials(member.full_name)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.full_name}
                          </div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {member.roles && member.roles.length > 0 ? (
                          member.roles.map((roleName, idx) => (
                            <span
                              key={idx}
                              className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800"
                            >
                              {roleName}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">Sem função</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(member.is_active)}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                      {formatDate(member.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedMember(member)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Ver detalhes"
                        >
                          <EyeIcon className="size-4" />
                        </button>
                        {canManageMembers &&
                          member.id !== currentUser?.id &&
                          (member.is_active ? (
                            <button
                              onClick={() => deactivateUserMutation.mutate(member.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Desativar"
                            >
                              <UserXIcon className="size-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => activateUserMutation.mutate(member.id)}
                              className="text-green-500 hover:text-green-700"
                              title="Ativar"
                            >
                              <UserCheckIcon className="size-4" />
                            </button>
                          ))}
                        {canManageMembers && member.id !== currentUser?.id && (
                          <button
                            onClick={() => setMemberToRemove(member)}
                            className="text-red-400 hover:text-red-600"
                            title="Remover membro"
                          >
                            <Trash2Icon className="size-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredUsers.length === 0 && (
            <div className="py-8 text-center text-gray-500">
              Nenhum membro encontrado com os filtros selecionados.
            </div>
          )}
        </div>
      )}

      {/* Member Detail Modal */}
      {selectedMember && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="mobile-modal-surface w-full max-w-3xl overflow-hidden rounded-lg bg-white">
            <div className="flex items-center justify-between border-b p-6">
              <div className="flex items-center space-x-4">
                {selectedMember.profile_photo_url ? (
                  <img
                    className="size-12 rounded-full object-cover"
                    src={selectedMember.profile_photo_url}
                    alt=""
                  />
                ) : (
                  <div className="flex size-12 items-center justify-center rounded-full bg-purple-100">
                    <span className="font-bold text-purple-700">
                      {getInitials(selectedMember.full_name)}
                    </span>
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-semibold">{selectedMember.full_name}</h2>
                  <p className="text-gray-600">{selectedMember.email}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedMember(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="size-6" />
              </button>
            </div>
            <div className="max-h-[calc(90vh-140px)] space-y-6 overflow-y-auto p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <h3 className="mb-4 font-medium">Informações</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <MailIcon className="size-4 text-gray-400" />
                      <span className="text-sm">{selectedMember.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CalendarIcon className="size-4 text-gray-400" />
                      <span className="text-sm">
                        Entrou em {formatDate(selectedMember.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      {selectedMember.is_active ? (
                        <CheckCircleIcon className="size-4 text-green-500" />
                      ) : (
                        <XCircleIcon className="size-4 text-red-500" />
                      )}
                      <span className="text-sm">
                        {selectedMember.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    {selectedMember.email_verified !== undefined && (
                      <div className="flex items-center space-x-3">
                        {selectedMember.email_verified ? (
                          <CheckCircleIcon className="size-4 text-green-500" />
                        ) : (
                          <AlertCircleIcon className="size-4 text-yellow-500" />
                        )}
                        <span className="text-sm">
                          {selectedMember.email_verified
                            ? 'Email verificado'
                            : 'Email não verificado'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="mb-4 font-medium">Funções e Permissões</h3>
                  <div className="space-y-3">
                    {selectedMemberRoles.length > 0 ? (
                      selectedMemberRoles.map((role) => (
                        <div key={role.id} className="rounded-lg border border-gray-200 p-3">
                          <div className="mb-2 flex items-center space-x-2">
                            <ShieldIcon className="size-4 text-purple-600" />
                            <span className="text-sm font-medium">{role.name}</span>
                            {role.is_system && <LockIcon className="size-3 text-gray-400" />}
                          </div>
                          {role.permissions && role.permissions.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {role.permissions.slice(0, 4).map((p) => (
                                <span
                                  key={p.id}
                                  className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                                >
                                  {p.name}
                                </span>
                              ))}
                              {role.permissions.length > 4 && (
                                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                                  +{role.permissions.length - 4}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="text-sm text-gray-400">Nenhuma função atribuída</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t bg-gray-50 p-6 sm:flex-row sm:justify-between sm:gap-0">
              {canManageMembers && selectedMember.id !== currentUser?.id ? (
                <button
                  onClick={() => setMemberToRemove(selectedMember)}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-800"
                >
                  <Trash2Icon className="size-4" />
                  Remover da organização
                </button>
              ) : (
                <div />
              )}
              <button
                onClick={() => setSelectedMember(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirm Modal */}
      <ConfirmModal
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        onConfirm={() => memberToRemove && removeMemberMutation.mutate(memberToRemove.id)}
        title="Remover membro"
        message={`Tem certeza que deseja remover ${memberToRemove?.full_name || memberToRemove?.email} da organização? O usuário perderá acesso a todos os recursos.`}
        confirmText="Remover"
        variant="danger"
        isLoading={removeMemberMutation.isPending}
      />
    </div>
  )

  const renderRolesTab = () => (
    <div className="space-y-6">
      {/* Header Actions */}
      <ResponsiveActionRow>
        <div className="flex w-full sm:w-auto">
          <div className="relative">
            <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
            <input
              type="text"
              placeholder="Buscar funções..."
              className="w-full rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:ring-2 focus:ring-purple-500 sm:w-auto"
            />
          </div>
        </div>
        {canManageRoles && (
          <button
            onClick={() => setShowCreateRoleModal(true)}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 sm:w-auto"
          >
            <PlusIcon className="size-4" />
            <span>Nova Função</span>
          </button>
        )}
      </ResponsiveActionRow>

      {/* Roles Grid */}
      {rolesLoading ? (
        <div className="flex justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {roles.map((role) => (
            <div key={role.id} className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-purple-100 p-2">
                    <ShieldIcon className="size-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{role.name}</h3>
                    <p className="text-sm text-gray-600">{role.description || 'Sem descrição'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {role.target_org_type && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        role.target_org_type === 'cloud_provider'
                          ? 'bg-blue-100 text-blue-700'
                          : role.target_org_type === 'reseller'
                            ? 'bg-amber-100 text-amber-700'
                            : role.target_org_type === 'end_customer'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {role.target_org_type === 'cloud_provider'
                        ? 'CP'
                        : role.target_org_type === 'reseller'
                          ? 'RS'
                          : role.target_org_type === 'end_customer'
                            ? 'EC'
                            : role.target_org_type}
                    </span>
                  )}
                  {role.is_system && (
                    <span title="Função do sistema">
                      <LockIcon className="size-4 text-gray-400" />
                    </span>
                  )}
                  <button
                    onClick={() => setSelectedRole(role)}
                    className="text-purple-600 hover:text-purple-900"
                  >
                    <EyeIcon className="size-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Membros:</span>
                  <span className="font-medium">{(role as any).user_count ?? '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Permissões:</span>
                  <span className="font-medium">{role.permissions?.length || 0}</span>
                </div>
              </div>

              <div className="mt-4">
                <h4 className="mb-2 text-xs font-medium text-gray-500">PRINCIPAIS PERMISSÕES</h4>
                <div className="flex flex-wrap gap-1">
                  {role.permissions?.slice(0, 3).map((p) => (
                    <span
                      key={p.id}
                      className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600"
                    >
                      {p.name}
                    </span>
                  ))}
                  {(role.permissions?.length || 0) > 3 && (
                    <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">
                      +{role.permissions!.length - 3} mais
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Role Detail Modal */}
      {selectedRole && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="mobile-modal-surface w-full max-w-4xl overflow-hidden rounded-lg bg-white">
            <div className="flex items-center justify-between border-b p-6">
              <div className="flex items-center space-x-4">
                <div className="rounded-lg bg-purple-100 p-3">
                  <ShieldIcon className="size-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{selectedRole.name}</h2>
                  <p className="text-gray-600">{selectedRole.description || 'Sem descrição'}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedRole(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="size-6" />
              </button>
            </div>
            <div className="max-h-[calc(90vh-140px)] space-y-6 overflow-y-auto p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
                <div className="rounded-lg bg-gray-50 p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {(selectedRole as any).user_count ?? '-'}
                  </div>
                  <div className="text-sm text-gray-600">Membros</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedRole.permissions?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Permissões</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedRole.is_system ? 'Sistema' : 'Customizada'}
                  </div>
                  <div className="text-sm text-gray-600">Tipo</div>
                </div>
              </div>

              <div>
                <h3 className="mb-4 font-medium">Permissões da Função</h3>
                <div className="grid grid-cols-1 gap-3">
                  {permissions.map((permission) => {
                    const hasPermission = selectedRole.permissions?.some(
                      (p) => p.id === permission.id,
                    )
                    return (
                      <div
                        key={permission.id}
                        className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex items-center space-x-3">
                          {hasPermission ? (
                            <CheckCircleIcon className="size-5 text-green-500" />
                          ) : (
                            <XCircleIcon className="size-5 text-gray-300" />
                          )}
                          <div>
                            <span className="font-medium">{permission.name}</span>
                            <p className="text-sm text-gray-500">{permission.description}</p>
                          </div>
                        </div>
                        <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">
                          {permission.resource}:{permission.action}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t bg-gray-50 p-6 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
              <button
                onClick={() => setSelectedRole(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  const renderPermissionsTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Gerenciamento de Permissões</h3>
          <p className="text-sm text-gray-600">Visualize as permissões disponíveis no sistema</p>
        </div>
      </div>

      {/* Permissions by Resource */}
      {permissionsLoading ? (
        <div className="flex justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(permissionsByResource)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([resource, perms]) => (
              <div key={resource} className="rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-200 px-6 py-4">
                  <h4 className="font-semibold text-gray-900 capitalize">{resource}</h4>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 gap-4">
                    {perms.map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="rounded-lg bg-blue-100 p-2">
                            <KeyIcon className="size-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{permission.name}</span>
                            </div>
                            <p className="text-sm text-gray-500">{permission.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="rounded bg-purple-100 px-2 py-1 font-mono text-xs text-purple-700">
                            {permission.resource}:{permission.action}
                          </span>
                          <span className="text-sm text-gray-500">
                            {getRolesUsingPermission(permission.id)} funções
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  )

  const renderAuditTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <ResponsiveActionRow>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Audit Trail</h3>
          <p className="text-sm text-gray-600">
            Histórico de ações de segurança e gerenciamento da organização
          </p>
        </div>
        <div className="w-full sm:w-auto">
          <select
            value={auditSource}
            onChange={(e) => setAuditSource(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500 sm:w-auto"
          >
            <option value="">Todas as fontes</option>
            <option value="rbac">RBAC</option>
            <option value="user">Usuário</option>
            <option value="auth">Autenticação</option>
            <option value="security">Segurança</option>
            <option value="organization">Organização</option>
          </select>
        </div>
      </ResponsiveActionRow>

      {/* Audit Log Table */}
      {auditLoading ? (
        <div className="flex justify-center py-12">
          <Loader2Icon className="size-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="divide-y divide-gray-200 md:hidden">
            {auditLogs.map((log) => (
              <div key={log.id} className="space-y-2 p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                    {log.action}
                  </span>
                  <span className={`rounded px-2 py-1 text-xs ${getLevelBadge(log.level)}`}>
                    {log.level}
                  </span>
                </div>
                <div className="text-sm text-gray-900">{formatDateTime(log.created_at)}</div>
                <div className="text-sm text-gray-700">
                  {log.user?.full_name || log.user?.email || '-'}
                </div>
                <div className="text-xs text-gray-600">
                  {log.resource_type
                    ? `${log.resource_type}${log.resource_id ? ` #${log.resource_id.slice(0, 8)}` : ''}`
                    : '-'}
                </div>
                <p className="text-sm text-gray-500">{log.message}</p>
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Ação
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Ator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Alvo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Mensagem
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Nível
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">
                      {formatDateTime(log.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">
                      {log.user?.full_name || log.user?.email || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">
                      {log.resource_type ? (
                        <span className="text-xs">
                          {log.resource_type}
                          {log.resource_id && (
                            <span className="ml-1 text-gray-400">
                              #{log.resource_id.slice(0, 8)}
                            </span>
                          )}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-500">
                      {log.message}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`rounded px-2 py-1 text-xs ${getLevelBadge(log.level)}`}>
                        {log.level}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {auditLogs.length === 0 && (
            <div className="py-8 text-center text-gray-500">Nenhum log encontrado.</div>
          )}
          {auditLogs.length >= auditLimit && (
            <div className="border-t py-4 text-center">
              <button
                onClick={() => setAuditLimit((prev) => prev + 50)}
                className="text-sm font-medium text-purple-600 hover:text-purple-800"
              >
                Carregar mais
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Gestão de Equipe"
        description="Gerencie membros, funções e permissões da sua organização"
        icon={UsersIcon}
      />

      {/* Tabs */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="px-4 pt-4 md:px-6 md:pt-6">
          <ResponsiveTabs
            tabs={TEAM_TABS}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel="Abas de equipe"
          />
        </div>

        <div className="p-6">
          {activeTab === 'members' && renderMembersTab()}
          {activeTab === 'roles' && renderRolesTab()}
          {activeTab === 'permissions' && renderPermissionsTab()}
          {activeTab === 'audit' && renderAuditTab()}
        </div>
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="mobile-modal-surface w-full max-w-md rounded-lg bg-white">
            <div className="p-6">
              <div className="mb-6 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Convidar Novo Membro</h3>
                <button
                  onClick={() => {
                    setShowInviteModal(false)
                    setInviteError('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="size-5" />
                </button>
              </div>

              <p className="mb-4 text-sm text-gray-500">
                Um email será enviado com um link para aceitar o convite. Se o destinatário ainda
                não tiver conta, poderá criar uma ao aceitar.
              </p>

              {inviteError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {inviteError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="invite-email-input"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <input
                    id="invite-email-input"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="usuario@empresa.com"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="invite-role-select"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Função *
                  </label>
                  <select
                    id="invite-role-select"
                    aria-label="Função do convite"
                    value={inviteForm.role_id}
                    onChange={(e) => setInviteForm({ ...inviteForm, role_id: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Selecione uma função</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
                <button
                  onClick={() => {
                    setShowInviteModal(false)
                    setInviteError('')
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => createInvitationMutation.mutate(inviteForm)}
                  disabled={
                    !inviteForm.email || !inviteForm.role_id || createInvitationMutation.isPending
                  }
                  className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {createInvitationMutation.isPending ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : (
                    <MailIcon className="size-4" />
                  )}
                  <span>Enviar Convite</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Role Modal */}
      {showCreateRoleModal && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="mobile-modal-surface w-full max-w-lg overflow-hidden rounded-lg bg-white">
            <div className="p-6">
              <div className="mb-6 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Nova Função</h3>
                <button
                  onClick={() => {
                    setShowCreateRoleModal(false)
                    setRoleError('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="size-5" />
                </button>
              </div>

              {roleError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {roleError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Nome</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    placeholder="Nome da função"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                    placeholder="Descrição da função"
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Permissões</label>
                  <div className="max-h-60 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
                    {permissions.map((p) => (
                      <label key={p.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={roleForm.permission_ids.includes(p.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRoleForm({
                                ...roleForm,
                                permission_ids: [...roleForm.permission_ids, p.id],
                              })
                            } else {
                              setRoleForm({
                                ...roleForm,
                                permission_ids: roleForm.permission_ids.filter((id) => id !== p.id),
                              })
                            }
                          }}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">{p.name}</span>
                        <span className="text-xs text-gray-400">
                          ({p.resource}:{p.action})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-3">
                <button
                  onClick={() => {
                    setShowCreateRoleModal(false)
                    setRoleError('')
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => createRoleMutation.mutate(roleForm)}
                  disabled={!roleForm.name || createRoleMutation.isPending}
                  className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {createRoleMutation.isPending ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : (
                    <PlusIcon className="size-4" />
                  )}
                  <span>Criar Função</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeamPage
